'use client';

import { ReactNode, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ReactLenis, useLenis, type LenisRef } from 'lenis/react';
import DesktopLayout from './layout/Desktop';
import { ProjectModel } from '@/lib/models/Project';
import { SizeProvider } from '@/components/SizeProvider';

function ScrollResetOnRouteChange() {
  const pathname = usePathname();
  const lenis = useLenis();

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenis]);

  return null;
}

interface Props {
  children: ReactNode;
  projects: ProjectModel[];
}

export function DesktopShell({ children, projects }: Props) {
  const lenisRef = useRef<LenisRef>(null);

  // Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
  useLenis(ScrollTrigger.update);

  useEffect(() => {
    function update(time: number) {
      lenisRef.current?.lenis?.raf(time * 1000);
    }

    gsap.ticker.add(update);
    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(update);
      gsap.ticker.lagSmoothing(500, 33);
    };
  }, []);

  return (
    <ReactLenis root ref={lenisRef} options={{ autoRaf: false }}>
      <ScrollResetOnRouteChange />
      <SizeProvider>
        <DesktopLayout projects={projects}>{children}</DesktopLayout>
      </SizeProvider>
    </ReactLenis>
  );
}
